import styled from "@emotion/styled";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { UsersFour } from "@phosphor-icons/react";
import { FemaleIcon } from "assets/icons/FemaleIcon";
import { MaleIcon } from "assets/icons/MaleIcon";
import { Button, EthiopianDatePicker } from "components/UI";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import {
  useDenominationDashboardMemberGrowth,
  useDenominationDashboardSavedBaptized,
  useDenominationDashboardSummary,
  useDenominationSummary,
  useReportsTimeline
} from "../api";
import { useDenominationStore } from "../store";

const StyledCard = styled(Stack)`
  padding: 8px;
  border-radius: 10px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
`;

const IconCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: grid;
  place-items: center;
  color: white;
  align-self: flex-end;
`;

const IconPerons = styled.div`
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  color: ${({ color }) => color};
  align-self: flex-start;
`;

const StatCardTop = styled.div`
  background-repeat: no-repeat;
  background-size: cover;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
`;

const StatCardBottom = styled.div`
  background-repeat: no-repeat;
  background-size: cover;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
`;

const donutDataLeft = ({ male, female }) => [
  { name: "Male", value: male, color: "#4285F4" },
  { name: "Female", value: female, color: "#F6C048" }
];

const donutDataRight = ({
  resigned_with_letter,
  returned_to_world,
  deceased,
  unknown_reason
}) => [
  { name: "Group 1", value: resigned_with_letter, color: "#4285F4" },
  { name: "Group 2", value: returned_to_world, color: "#A259FF" },
  { name: "Group 3", value: deceased, color: "#F6C048" },
  { name: "Group 4", value: unknown_reason, color: "#2AD2C9" }
];

const COLORS = ["#4285F4", "#F6C048", "#A259FF", "#2AD2C9"];

// Custom tooltip for area chart to show male/female breakdown
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#F69148", fontWeight: "bold" }}
        >
          Total: {data.added}
        </Typography>
        <Stack direction="row" spacing={2} mt={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              Male: {data.male}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              Female: {data.female}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }
  return null;
};

// Transform dashboardMemberGrowth data for the area chart
const transformMemberGrowthData = dashboardMemberGrowth => {
  if (!dashboardMemberGrowth) return [];

  const quarters = Object.keys(dashboardMemberGrowth)
    .filter(key => key !== "total_members_added")
    .sort(); // Sort to ensure proper chronological order

  return quarters.map(quarter => ({
    name: quarter.replace("-", " "), // Convert "2024-Q1" to "2024 Q1"
    added: dashboardMemberGrowth[quarter]?.total || 0,
    male: dashboardMemberGrowth[quarter]?.male || 0,
    female: dashboardMemberGrowth[quarter]?.female || 0
  }));
};

// Transform dashboardSavedBaptized data for the bar chart
const transformSavedBaptizedData = dashboardSavedBaptized => {
  if (!dashboardSavedBaptized) return [];

  const quarters = Object.keys(dashboardSavedBaptized).sort(); // Sort to ensure proper chronological order

  return quarters.map(quarter => ({
    name: quarter.replace("-", " "), // Convert "2024-Q1" to "2024 Q1"
    male: dashboardSavedBaptized[quarter]?.male || 0,
    female: dashboardSavedBaptized[quarter]?.female || 0
  }));
};

export const DenominationMembersDashboard = () => {
  const [startDate, setStartDate] = useQueryState("startDate", parseAsString);
  const [endDate, setEndDate] = useQueryState("endDate", parseAsString);
  const [selectedTitles, setSelectedTitles] = useQueryState(
    "title",
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { t } = useTranslation();

  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );

  const { data: dashboardSummary } = useDenominationDashboardSummary({
    denominationId: selectedDenomination?.id,
    startDate,
    endDate
  });

  const { data: dashboardMemberGrowth } = useDenominationDashboardMemberGrowth({
    denominationId: selectedDenomination?.id,
    startDate,
    endDate
  });

  const { data: dashboardSavedBaptized } =
    useDenominationDashboardSavedBaptized({
      denominationId: selectedDenomination?.id,
      startDate,
      endDate
    });

  const { data: summaryData } = useDenominationSummary({
    denominationId: selectedDenomination?.id,
    startDate,
    endDate
  });

  const { data: reportsTimeline } = useReportsTimeline({
    denominationId: selectedDenomination?.id
  });

  const handleTitleChange = selectedTitleIds => {
    setSelectedTitles(selectedTitleIds);

    const selectedReports = reportsTimeline?.filter(report =>
      selectedTitleIds.includes(report.id)
    );

    if (!selectedReports || selectedReports.length === 0) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    // Find earliest start date and latest end date
    const sortedByStart = [...selectedReports].sort(
      (a, b) => new Date(a.report_start_date) - new Date(b.report_start_date)
    );
    const sortedByEnd = [...selectedReports].sort(
      (a, b) => new Date(b.report_end_date) - new Date(a.report_end_date)
    );

    setStartDate(sortedByStart[0].report_start_date);
    setEndDate(sortedByEnd[0].report_end_date);
  };

  const parentStructureTypeName =
    summaryData?.structure_type_hierarchy?.name || "";

  return (
    <Stack py={isSmDown ? 2 : 4} spacing={isSmDown ? 2 : 3}>
      <Stack spacing={1}>
        <Typography
          sx={{ fontWeight: "bold", fontSize: isSmDown ? 18 : undefined }}
        >
          {selectedDenomination?.name}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ color: "#f69148", fontSize: isSmDown ? 14 : undefined }}
        >
          {t("denominations.memberDashboard.title")}
        </Typography>
      </Stack>
      <Stack
        direction={isSmDown ? "column" : "row"}
        alignItems={isSmDown ? "flex-start" : "center"}
        spacing={2}
      >
        <FormControl sx={{ width: "280px" }}>
          <InputLabel shrink size="small">
            {t("denominations.memberDashboard.reportTitle")}
          </InputLabel>
          <Select
            size="small"
            label={t("denominations.memberDashboard.reportTitle")}
            notched
            displayEmpty
            multiple
            value={selectedTitles}
            onChange={e => handleTitleChange(e.target.value)}
            renderValue={selected => {
              if (!selected || selected.length === 0) {
                return (
                  <span style={{ color: "#aaa" }}>
                    {t("denominations.memberDashboard.selectOneOrMoreTitles")}
                  </span>
                );
              }
              // Find the titles for the selected ids
              const selectedTitlesLabels = reportsTimeline
                ?.filter(report => selected.includes(report.id))
                .map(report => report.title || "No title");
              return selectedTitlesLabels?.join(", ");
            }}
          >
            <MenuItem value="" disabled>
              {t("denominations.memberDashboard.selectOneOrMoreTitles")}
            </MenuItem>
            {reportsTimeline?.map(report => (
              <MenuItem key={report.id} value={report.id}>
                {report.title || t("denominations.memberDashboard.noTitle")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <EthiopianDatePicker
          label={t("denominations.memberDashboard.startDate")}
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          width="280px"
          disabled
        />
        <EthiopianDatePicker
          label={t("denominations.memberDashboard.endDate")}
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          width="280px"
          disabled
        />
        {startDate && endDate && (
          <Button
            flavor="outlined"
            onClick={() => {
              handleTitleChange([]);
            }}
            style={{
              borderColor: "rgba(0, 0, 0, 0.6)",
              color: "rgba(0, 0, 0, 0.6)"
            }}
          >
            {t("denominations.memberDashboard.clear")}
          </Button>
        )}
      </Stack>
      {/* First section card: StatCards row, then line chart + members added card */}
      <Stack
        sx={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          pt: isSmDown ? 1 : 2,
          pb: isSmDown ? 2 : 5,
          px: isSmDown ? 1 : 3,
          boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.1)",
          mb: isSmDown ? 2 : 4
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mb: 2,
            fontSize: isSmDown ? 16 : undefined
          }}
        >
          በ{parentStructureTypeName} ያሉ አባላት ብዛት
        </Typography>
        <Box
          mt={2}
          sx={{
            display: "grid",
            gridTemplateColumns: isXs
              ? "1fr"
              : isSm
              ? "repeat(1, 1fr)"
              : isMd
              ? "repeat(3, 1fr)"
              : isLg
              ? "repeat(4, 1fr)"
              : "repeat(5, 1fr)",
            gap: 2
          }}
        >
          <StatCard
            title={`በ${parentStructureTypeName} ያሉ አጠቃላይ ምእመናን ብዛት`}
            stat={dashboardSummary?.members?.total?.total || 0}
            icon={<UsersFour size={24} />}
            color="#f69148"
            maleCount={dashboardSummary?.members?.total?.male || 0}
            femaleCount={dashboardSummary?.members?.total?.female || 0}
          />
          <StatCard
            title={`በ${parentStructureTypeName} ያሉ የተጠማቂ አባላት ብዛት`}
            stat={dashboardSummary?.members?.baptized?.total || 0}
            icon={<UsersFour size={24} />}
            color="#f69148"
            maleCount={dashboardSummary?.members?.baptized?.male || 0}
            femaleCount={dashboardSummary?.members?.baptized?.female || 0}
          />
          <StatCard
            title="ያልተጠመቁ ወገኖች ብዛት"
            stat={dashboardSummary?.members?.unbaptized?.total || 0}
            icon={<UsersFour size={24} />}
            color="#f69148"
            maleCount={dashboardSummary?.members?.unbaptized?.male || 0}
            femaleCount={dashboardSummary?.members?.unbaptized?.female || 0}
          />
          <StatCard
            title="የልጆች ብዛት"
            stat={dashboardSummary?.children?.total || 0}
            icon={<UsersFour size={24} />}
            color="#f69148"
            maleCount={dashboardSummary?.children?.male || 0}
            femaleCount={dashboardSummary?.children?.female || 0}
          />
          <StatCard
            title="ከሌሎች ቤተ አምነቶች ወደ ቤ/ክ የመጡ አማኞች ብዛት"
            stat={dashboardSummary?.migrated_believers?.total || 0}
            icon={<UsersFour size={24} />}
            color="#f69148"
            maleCount={dashboardSummary?.migrated_believers?.male || 0}
            femaleCount={dashboardSummary?.migrated_believers?.female || 0}
          />
        </Box>
        {/* Line chart and members added card */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mt: 5,
            mb: 2,
            fontSize: isSmDown ? 16 : undefined
          }}
        >
          የአባላት እድገት ሁኔታ
        </Typography>
        <Stack
          direction={isSmDown ? "column" : "row"}
          spacing={2}
          alignItems="center"
        >
          <Box flex={3} minWidth={0} width={isSmDown ? "100%" : undefined}>
            <ResponsiveContainer width="100%" height={isSmDown ? 180 : 250}>
              <AreaChart
                data={transformMemberGrowthData(dashboardMemberGrowth)}
                margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip content={<CustomAreaTooltip />} />
                <Area
                  type="monotone"
                  dataKey="added"
                  stroke="#F69148"
                  fill="#FFE0C2"
                  strokeWidth={3}
                  activeDot={{ r: 8 }}
                  dot={{
                    r: 6,
                    fill: "#F69148",
                    stroke: "#fff",
                    strokeWidth: 2
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={isSmDown ? "100%" : undefined}
            mt={isSmDown ? 2 : 0}
          >
            <MembersAddedCard
              total={dashboardMemberGrowth?.total_members_added || 0}
            />
          </Box>
        </Stack>
      </Stack>

      <Stack
        sx={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          pt: isSmDown ? 1 : 2,
          pb: isSmDown ? 2 : 5,
          px: isSmDown ? 1 : 3,
          boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.1)"
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bold",
            mb: 2,
            fontSize: isSmDown ? 16 : undefined
          }}
        >
          የዳኑና የውሃ ጥምቀት በመውሰድ ወደ ቤ/ክ የተቀላቀሉ ብዛት
        </Typography>
        <Stack direction={isSmDown ? "column" : "row"} spacing={3}>
          {/* Left: Chart */}
          <Stack flex={2} minWidth={0} width={isSmDown ? "100%" : undefined}>
            <MembersBarChart
              data={transformSavedBaptizedData(dashboardSavedBaptized)}
              isSmDown={isSmDown}
            />
          </Stack>
          {/* Right: Stat Cards */}
          <Stack
            flex={1}
            spacing={2}
            width={isSmDown ? "100%" : undefined}
            mt={isSmDown ? 2 : 0}
          >
            <Stack>
              <Stack
                direction={isSmDown ? "column" : "row"}
                justifyContent="space-between"
                spacing={2}
                mb={2}
              >
                <StatCard
                  title="ጌታን የተቀበሉ ብዛት"
                  stat={dashboardSummary?.newcomers_accepted?.total || 0}
                  icon={<UsersFour size={24} />}
                  color="#f6c048"
                  maleCount={dashboardSummary?.newcomers_accepted?.male || 0}
                  femaleCount={
                    dashboardSummary?.newcomers_accepted?.female || 0
                  }
                />
                <StatCard
                  title="የድነት ትምህርት ተምረው የውሃ ጥምቀት የወሰዱ"
                  stat={dashboardSummary?.newcomers_baptized?.total || 0}
                  icon={<UsersFour size={24} />}
                  color="#4a90e2"
                  maleCount={dashboardSummary?.newcomers_baptized?.male || 0}
                  femaleCount={
                    dashboardSummary?.newcomers_baptized?.female || 0
                  }
                />
              </Stack>
              <StatCard
                title="አጠቃላይ ድምር"
                stat={dashboardSummary?.newcomers_total?.total || 0}
                icon={<UsersFour size={24} />}
                color="#000000"
                maleCount={dashboardSummary?.newcomers_total?.male || 0}
                femaleCount={dashboardSummary?.newcomers_total?.female || 0}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* Donut chart sections */}
      <Stack direction={isSmDown ? "column" : "row"} spacing={4} mt={4}>
        {/* Left Donut Card */}
        <Stack
          flex={1}
          sx={{
            background: "#fff",
            borderRadius: "20px",
            p: isSmDown ? 2 : 4,
            boxShadow: "0px 0px 2px 0px rgba(0,0,0,0.1)",
            mb: isSmDown ? 2 : 0
          }}
          alignItems="center"
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              alignSelf: "flex-start",
              fontSize: isSmDown ? 16 : undefined
            }}
          >
            ከሌሎች ቤተ እምነቶች በሸኚ ወደ ቤ/ክ የመጡ አማኞች ብዛት
          </Typography>
          <Stack
            alignItems="center"
            justifyContent="center"
            flex={1}
            width="100%"
          >
            <DonutChart
              data={donutDataLeft({
                male: dashboardSummary?.migrated_believers?.male || 0,
                female: dashboardSummary?.migrated_believers?.female || 0
              })}
              value={dashboardSummary?.migrated_believers?.total || 0}
              width={isSmDown ? 160 : 220}
              height={isSmDown ? 160 : 220}
              valueFontSize={isSmDown ? 28 : 48}
              labelFontSize={isSmDown ? 12 : 18}
            />
            <Stack
              width="100%"
              mt={3}
              alignItems="center"
              justifyContent="center"
            >
              <StatCard
                title="ከሌሎች ቤተ አምነቶች ወደ ቤ/ክ የመጡ አማኞች"
                stat={dashboardSummary?.migrated_believers?.total || 0}
                icon={<UsersFour size={24} />}
                color="#4285F4"
                maleCount={dashboardSummary?.migrated_believers?.male || 0}
                femaleCount={dashboardSummary?.migrated_believers?.female || 0}
                maleIconColor="#4285F4"
                femaleIconColor="#4285F4"
              />
            </Stack>
          </Stack>
        </Stack>
        {/* Right Donut Card */}
        <Stack
          flex={1}
          sx={{
            background: "#fff",
            borderRadius: "20px",
            p: isSmDown ? 2 : 4,
            boxShadow: "0px 0px 2px 0px rgba(0,0,0,0.1)"
          }}
          alignItems="center"
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mb: 2,
              alignSelf: "flex-start",
              fontSize: isSmDown ? 16 : undefined
            }}
          >
            በልዩ ልዩ ምክንያቶች አሁን በ{parentStructureTypeName} የሌሉ አባላትን ብዛት
          </Typography>
          <DonutChart
            data={donutDataRight({
              resigned_with_letter:
                dashboardSummary?.resigned_with_letter?.total || 0,
              returned_to_world:
                dashboardSummary?.returned_to_world?.total || 0,
              deceased: dashboardSummary?.deceased?.total || 0,
              unknown_reason: dashboardSummary?.unknown_reason?.total || 0
            })}
            value={
              (dashboardSummary?.resigned_with_letter?.total || 0) +
              (dashboardSummary?.returned_to_world?.total || 0) +
              (dashboardSummary?.deceased?.total || 0) +
              (dashboardSummary?.unknown_reason?.total || 0)
            }
            valueLabel="አጠቃላይ ድምር"
            width={isSmDown ? 160 : 220}
            height={isSmDown ? 160 : 220}
            valueFontSize={isSmDown ? 28 : 40}
            labelFontSize={isSmDown ? 12 : 16}
          />
          <Box width="100%" mt={3}>
            <Stack direction={isSmDown ? "column" : "row"} spacing={2} mb={2}>
              <StatCard
                title="በሸኚ የለቀቁ ብዛት"
                stat={dashboardSummary?.resigned_with_letter?.total || 0}
                icon={<UsersFour size={24} />}
                color="#4285F4"
                maleCount={dashboardSummary?.resigned_with_letter?.male || 0}
                femaleCount={
                  dashboardSummary?.resigned_with_letter?.female || 0
                }
                maleIconColor="#4285F4"
                femaleIconColor="#4285F4"
              />
              <StatCard
                title="ወደ ዓለም የተመለሱ ብዛት"
                stat={dashboardSummary?.returned_to_world?.total || 0}
                icon={<UsersFour size={24} />}
                color="#A259FF"
                maleCount={dashboardSummary?.returned_to_world?.male || 0}
                femaleCount={dashboardSummary?.returned_to_world?.female || 0}
                maleIconColor="#A259FF"
                femaleIconColor="#A259FF"
              />
            </Stack>
            <Stack direction={isSmDown ? "column" : "row"} spacing={2}>
              <StatCard
                title="ከዚህ ዓለም በሞት የተለዩ ብዛት"
                stat={dashboardSummary?.deceased?.total || 0}
                icon={<UsersFour size={24} />}
                color="#F6C048"
                maleCount={dashboardSummary?.deceased?.male || 0}
                femaleCount={dashboardSummary?.deceased?.female || 0}
                maleIconColor="#F6C048"
                femaleIconColor="#F6C048"
              />
              <StatCard
                title="ባልታወቀው ምክንያት የለቀቁ ብዛት"
                stat={dashboardSummary?.unknown_reason?.total || 0}
                icon={<UsersFour size={24} />}
                color="#2AD2C9"
                maleCount={dashboardSummary?.unknown_reason?.male || 0}
                femaleCount={dashboardSummary?.unknown_reason?.female || 0}
                maleIconColor="#2AD2C9"
                femaleIconColor="#2AD2C9"
              />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

const StatCard = ({ title, stat, icon, color, maleCount, femaleCount }) => {
  return (
    <StyledCard sx={{ flex: 1 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Typography variant="subtitle1">{title}</Typography>
        <StatCardTop>
          <IconCircle color={color}>{icon}</IconCircle>
        </StatCardTop>
      </Stack>
      <Typography variant="h5" fontWeight="bold">
        {(stat || 0)?.toLocaleString()}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} mt="auto">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <StatCardBottom>
            <IconPerons color={color}>
              <MaleIcon color={color} />
            </IconPerons>
          </StatCardBottom>
          <Typography variant="subtitle1">
            {(maleCount || 0)?.toLocaleString()}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
          <StatCardBottom>
            <IconPerons color={color}>
              <FemaleIcon color={color} />
            </IconPerons>
          </StatCardBottom>
          <Typography variant="subtitle1">
            {(femaleCount || 0)?.toLocaleString()}
          </Typography>
        </Stack>
      </Stack>
    </StyledCard>
  );
};

const MembersBarChart = ({ data, isSmDown }) => (
  <ResponsiveContainer width="100%" height={isSmDown ? 180 : 300}>
    <BarChart data={data} barGap={0}>
      <CartesianGrid vertical={false} />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip />
      <Bar
        dataKey="male"
        fill="#4285F4"
        name="Male"
        radius={[50, 50, 0, 0]}
        barSize={isSmDown ? 16 : 24}
      />
      <Bar
        dataKey="female"
        fill="#F6C048"
        name="Female"
        radius={[50, 50, 0, 0]}
        barSize={isSmDown ? 16 : 24}
      />
    </BarChart>
  </ResponsiveContainer>
);

const MembersAddedCard = ({ total = 0 }) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      sx={{
        background: "#F69148",
        borderRadius: "12px",
        color: "#fff",
        p: 2,
        minWidth: 230,
        alignItems: "center",
        justifyContent: "space-between",
        height: "100%"
      }}
      spacing={1}
    >
      <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 500 }}>
        {t("denominations.memberDashboard.membersAdded")}
      </Typography>
      <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700 }}>
        {total || 0}
      </Typography>
    </Stack>
  );
};

const DonutChart = ({
  data,
  value,
  valueLabel,
  width = 220,
  height = 220,
  valueFontSize = 48,
  labelFontSize = 18
}) => {
  // Custom label renderer for perfect centering
  const renderCenterLabel = ({ cx, cy }) => (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={valueFontSize}
      fontWeight={700}
      fill="#181C32"
    >
      {value}
    </text>
  );

  // Responsive: use ResponsiveContainer on mobile
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  if (isSmDown) {
    return (
      <Box
        width="100%"
        maxWidth="90vw"
        mx="auto"
        display="flex"
        justifyContent="center"
      >
        <ResponsiveContainer width="100%" aspect={1}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              label={renderCenterLabel}
              labelLine={false}
            >
              {data.map((entry, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={entry.color || COLORS[idx % COLORS.length]}
                />
              ))}
            </Pie>
            {/* Render the bottom label separately for perfect placement */}
            {valueLabel && (
              <g>
                <text
                  x="50%"
                  y="60%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={labelFontSize}
                  fontWeight={400}
                  fill="#A3AED0"
                >
                  {valueLabel}
                </text>
              </g>
            )}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  return (
    <PieChart width={width} height={height}>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        innerRadius={70}
        outerRadius={100}
        dataKey="value"
        startAngle={90}
        endAngle={-270}
        stroke="none"
        label={renderCenterLabel}
        labelLine={false}
      >
        {data.map((entry, idx) => (
          <Cell
            key={`cell-${idx}`}
            fill={entry.color || COLORS[idx % COLORS.length]}
          />
        ))}
      </Pie>
      {/* Render the bottom label separately for perfect placement */}
      {valueLabel && (
        <g>
          <text
            x={width / 2}
            y={height / 2 + valueFontSize / 1.5}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={labelFontSize}
            fontWeight={400}
            fill="#A3AED0"
          >
            {valueLabel}
          </text>
        </g>
      )}
    </PieChart>
  );
};
